import api from "./axiosConfig";

export interface ConnectStatus {
  hasAccount: boolean;
  stripeAccountId?: string | null;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  onboardingComplete: boolean;
}

export interface ClientBillingPlan {
  id: string;
  name: string;
  stripePriceId: string;
  amountCents: number;
  currency: string;
  billingInterval: string;
  isActive: boolean;
}

export interface ClientBillingSubscriptionSummary {
  planName: string;
  amountCents: number;
  currency: string;
  status: string;
  currentPeriodEnd?: string | null;
}

export interface ClientBillingPaymentItem {
  id: string;
  amountCents: number;
  currency: string;
  status: string;
  paidAt?: string | null;
}

export interface ClientBillingRow {
  clientId: string;
  firstName: string;
  lastName: string;
  email: string;
  subscriptionStatus?: string | null;
  planName?: string | null;
  croaFirstServiceDate?: string | null;
}

export const clientBillingApi = {
  getConnectStatus: async (): Promise<ConnectStatus> => {
    const response = await api.get<ConnectStatus>("/clientbilling/connect/status");
    return response.data;
  },

  createConnectAccountLink: async (): Promise<{ url: string }> => {
    const response = await api.post<{ url: string }>("/clientbilling/connect/account-link", {});
    return response.data;
  },

  listPlans: async (): Promise<ClientBillingPlan[]> => {
    const response = await api.get<ClientBillingPlan[]>("/clientbilling/plans");
    return response.data;
  },

  createPlan: async (payload: {
    name: string;
    stripePriceId: string;
    amountCents: number;
    currency?: string;
    billingInterval?: string;
  }): Promise<ClientBillingPlan> => {
    const response = await api.post<ClientBillingPlan>("/clientbilling/plans", payload);
    return response.data;
  },

  listClients: async (): Promise<ClientBillingRow[]> => {
    const response = await api.get<ClientBillingRow[]>("/clientbilling/clients");
    return response.data;
  },

  getClientSubscription: async (clientId: string): Promise<ClientBillingSubscriptionSummary | null> => {
    try {
      const response = await api.get<ClientBillingSubscriptionSummary>(
        `/clientbilling/clients/${clientId}/subscription`
      );
      return response.data;
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return null;
      }
      throw e;
    }
  },

  getClientPayments: async (clientId: string): Promise<ClientBillingPaymentItem[]> => {
    const response = await api.get<ClientBillingPaymentItem[]>(`/clientbilling/clients/${clientId}/payments`);
    return response.data;
  },

  createCheckout: async (clientId: string, planId: string): Promise<{ url: string; sessionId: string }> => {
    const response = await api.post<{ url: string; sessionId: string }>(
      `/clientbilling/clients/${clientId}/checkout`,
      { planId }
    );
    return response.data;
  },
};
